import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Cookie, ShieldCheck, Settings2, BarChart3, Megaphone, Wrench, Globe2,
  Clock3, Mail, ScrollText, AlertTriangle, Lock, RefreshCcw,
} from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { useEffect, useState } from "react";
import { isTrackingOptedOut, setTrackingOptOut } from "@/lib/client-errors/tracker";

const SITE_URL = "https://fixhen.com";
const PAGE_URL = `${SITE_URL}/cookies`;
const CONTACT_EMAIL = "info@contact.fixhen.com";
const LAST_UPDATED = "1 July 2026";
const EFFECTIVE_DATE = "1 July 2026";

type Category = {
  id: string;
  icon: React.ReactNode;
  name: string;
  legalBasis: string;
  required: boolean;
  purpose: string;
  examples: { name: string; provider: string; purpose: string; duration: string; type: "First-party" | "Third-party" }[];
};

const CATEGORIES: Category[] = [
  {
    id: "strictly-necessary",
    icon: <ShieldCheck className="h-5 w-5" />,
    name: "Strictly necessary cookies",
    legalBasis: "Art. 5(3) ePrivacy Directive — exemption; no consent required.",
    required: true,
    purpose:
      "Enable core Site functionality: routing, session persistence, load balancing, cart contents, checkout state, authentication, CSRF protection and secure delivery of pages. The Site cannot function properly without them.",
    examples: [
      { name: "sb-*-auth-token", provider: "fixhen.com", purpose: "Authenticated session for signed-in customers.", duration: "1 year", type: "First-party" },
      { name: "fixhen.cart", provider: "fixhen.com (localStorage)", purpose: "Persist cart items between visits.", duration: "Until cleared", type: "First-party" },
      { name: "cookie_consent", provider: "fixhen.com", purpose: "Store your cookie preferences.", duration: "12 months", type: "First-party" },
      { name: "__cf_bm", provider: "Cloudflare", purpose: "Bot management and abuse protection.", duration: "30 minutes", type: "Third-party" },
    ],
  },
  {
    id: "functional",
    icon: <Settings2 className="h-5 w-5" />,
    name: "Functional cookies",
    legalBasis: "Consent — Art. 6(1)(a) GDPR & Art. 5(3) ePrivacy Directive.",
    required: false,
    purpose:
      "Remember preferences that enhance your experience: language, currency, saved vehicle for fitment, recently viewed products and interface choices. Refusing them does not block core functionality but may reduce personalisation.",
    examples: [
      { name: "fixhen.locale", provider: "fixhen.com", purpose: "Selected language and currency.", duration: "12 months", type: "First-party" },
      { name: "fixhen.garage", provider: "fixhen.com", purpose: "Saved vehicle for fitment filtering.", duration: "12 months", type: "First-party" },
      { name: "fixhen.recent", provider: "fixhen.com", purpose: "Recently viewed products carousel.", duration: "30 days", type: "First-party" },
    ],
  },
  {
    id: "analytics",
    icon: <BarChart3 className="h-5 w-5" />,
    name: "Analytics & performance cookies",
    legalBasis: "Consent — Art. 6(1)(a) GDPR & Art. 5(3) ePrivacy Directive.",
    required: false,
    purpose:
      "Collect aggregated, pseudonymous statistics about how visitors interact with the Site — pages viewed, load speed, click paths, error rates — so we can measure performance and improve the catalogue, search and checkout.",
    examples: [
      { name: "_ga, _ga_*", provider: "Google Analytics 4", purpose: "Distinguish visitors and sessions.", duration: "13 months", type: "Third-party" },
      { name: "_plausible", provider: "Plausible Analytics (EU-hosted)", purpose: "Aggregate, cookie-less traffic metrics.", duration: "24 hours", type: "Third-party" },
    ],
  },
  {
    id: "marketing",
    icon: <Megaphone className="h-5 w-5" />,
    name: "Marketing & advertising cookies",
    legalBasis: "Consent — Art. 6(1)(a) GDPR & Art. 5(3) ePrivacy Directive.",
    required: false,
    purpose:
      "Measure the effectiveness of our advertising campaigns and, where you have consented, deliver relevant remarketing on third-party platforms. Refusing them does not increase or decrease the number of ads you see — only their relevance.",
    examples: [
      { name: "_fbp", provider: "Meta (Facebook/Instagram)", purpose: "Conversion tracking and remarketing.", duration: "3 months", type: "Third-party" },
      { name: "_gcl_au", provider: "Google Ads", purpose: "Attribute conversions to Google Ads campaigns.", duration: "3 months", type: "Third-party" },
    ],
  },
  {
    id: "operational",
    icon: <Wrench className="h-5 w-5" />,
    name: "Operational third-party services",
    legalBasis: "Consent where required — Art. 5(3) ePrivacy Directive.",
    required: false,
    purpose:
      "Cookies and equivalent local-storage items set by embedded services we use to operate the Site: payment providers, fraud prevention, chat support, delivery tracking and 3D product viewers. Consent is requested where the underlying purpose is not strictly necessary.",
    examples: [
      { name: "__stripe_mid, __stripe_sid", provider: "Stripe", purpose: "Fraud prevention on checkout.", duration: "1 year / 30 min", type: "Third-party" },
      { name: "intercom-*", provider: "Intercom", purpose: "Persist support chat conversation.", duration: "9 months", type: "Third-party" },
    ],
  },
];

const FAQS: { q: string; a: string }[] = [
  {
    q: "Do I have to accept cookies to use the Site?",
    a: "No. Strictly necessary cookies are the only ones set without consent, in line with Article 5(3) of the ePrivacy Directive. All other categories require your explicit opt-in and can be refused without affecting your ability to browse, order or receive after-sales support.",
  },
  {
    q: "How do I withdraw or change my consent?",
    a: "Reopen the cookie preferences panel at any time via the ‘Cookie settings’ link in the footer, or by clearing your browser cookies. Withdrawal is as easy as giving consent and takes effect for the future only — it does not affect the lawfulness of processing carried out on the basis of the earlier consent.",
  },
  {
    q: "Are analytics cookies used to identify me personally?",
    a: "No. We configure analytics providers to pseudonymise identifiers, truncate IP addresses and disable cross-site tracking features where technically possible. Data is used in aggregate to improve the Site and is retained for the period stated in the cookie table.",
  },
  {
    q: "Do you transfer data outside the European Economic Area (EEA)?",
    a: "Some third-party providers listed in this policy may process data in third countries. We rely on the EU-US Data Privacy Framework, the European Commission's Standard Contractual Clauses (2021/914) and, where appropriate, supplementary technical measures. Details are available in our Privacy Policy or on request.",
  },
  {
    q: "What happens if I ignore the cookie banner?",
    a: "Only strictly necessary cookies are set. No optional cookies are placed until you make an active, informed choice. Continued browsing is not treated as consent under EDPB Guidelines 05/2020.",
  },
  {
    q: "Do you use dark patterns to obtain consent?",
    a: "No. Accept and Reject buttons are equally visible and require the same number of clicks. Pre-ticked boxes are not used; no default is set to ‘on’ for any non-essential category, in line with the CJEU Planet49 ruling (C-673/17).",
  },
];

export const Route = createFileRoute("/cookies")({
  head: () => {
    const jsonLd = [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Cookie Policy — FIXHEN",
        url: PAGE_URL,
        inLanguage: "en",
        isPartOf: { "@type": "WebSite", name: "FIXHEN", url: SITE_URL },
        about: { "@type": "Thing", name: "Cookies and similar tracking technologies" },
        datePublished: "2026-07-01",
        dateModified: "2026-07-01",
        publisher: { "@type": "Organization", name: "FIXHEN", url: SITE_URL },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
          { "@type": "ListItem", position: 2, name: "Cookie Policy", item: PAGE_URL },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQS.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ];
    return {
      meta: [
        { title: "Cookie Policy — GDPR & ePrivacy Compliant | FIXHEN" },
        {
          name: "description",
          content:
            "How FIXHEN uses cookies and similar technologies: categories, legal basis, retention, third-party providers and how to manage your consent. GDPR & ePrivacy compliant.",
        },
        { property: "og:title", content: "Cookie Policy — GDPR & ePrivacy Compliant | FIXHEN" },
        {
          property: "og:description",
          content: "Full transparency on cookies, tracking technologies and how to manage your consent at FIXHEN.",
        },
        { property: "og:type", content: "article" },
        { property: "og:url", content: PAGE_URL },
        { name: "twitter:title", content: "Cookie Policy — FIXHEN" },
        { name: "twitter:description", content: "Cookies, tracking technologies and how to manage your consent." },
        { name: "robots", content: "index, follow" },
        { property: "og:image", content: "https://fixhen.com/og/home.jpg" },
        { name: "twitter:image", content: "https://fixhen.com/og/home.jpg" },
      ],
      links: [{ rel: "canonical", href: PAGE_URL }],
      scripts: jsonLd.map((data) => ({
        type: "application/ld+json",
        children: JSON.stringify(data),
      })),
    };
  },
  component: CookiesPage,
});

function CookiesPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-5xl px-4 py-12 sm:px-6 lg:py-16">
        <nav aria-label="Breadcrumb" className="mb-6 text-xs text-muted-foreground">
          <ol className="flex items-center gap-2">
            <li><Link to="/" className="hover:text-foreground">Home</Link></li>
            <li aria-hidden>/</li>
            <li className="text-foreground">Cookie Policy</li>
          </ol>
        </nav>

        <header className="mb-10 border-b border-border pb-8">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-muted/50 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
            <Cookie className="h-3.5 w-3.5" /> Legal · Cookies & Tracking
          </div>
          <h1 className="mt-4 text-4xl font-black tracking-tight text-foreground sm:text-5xl">
            Cookie Policy
          </h1>
          <p className="mt-3 max-w-3xl text-lg text-muted-foreground">
            This Cookie Policy explains what cookies and similar technologies are, why FIXHEN
            uses them, what data they collect and how you can manage your preferences at any
            time. It is drafted to comply with the GDPR, the ePrivacy Directive 2002/58/EC as
            transposed in Bulgaria, and the guidance of the European Data Protection Board.
          </p>
          <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
            <span>Effective date: <strong className="text-foreground">{EFFECTIVE_DATE}</strong></span>
            <span>Last updated: <strong className="text-foreground">{LAST_UPDATED}</strong></span>
            <span>Version: <strong className="text-foreground">1.0</strong></span>
          </div>
        </header>

        <section className="mb-10 grid gap-3 sm:grid-cols-3">
          <SummaryCard icon={<ShieldCheck className="h-5 w-5" />} title="Consent-first" body="No non-essential cookie is set until you actively opt in." />
          <SummaryCard icon={<RefreshCcw className="h-5 w-5" />} title="Change anytime" body="Reopen the preferences panel from the footer — one click." />
          <SummaryCard icon={<Lock className="h-5 w-5" />} title="No dark patterns" body="Accept and Reject are equally prominent and equally easy." />
        </section>

        <article className="prose prose-slate max-w-none dark:prose-invert prose-headings:font-black prose-headings:tracking-tight prose-h2:mt-12 prose-h2:scroll-mt-24 prose-h2:text-2xl prose-h3:text-lg prose-a:text-primary prose-strong:text-foreground">
          <h2 id="what"><ScrollText className="mr-2 inline h-5 w-5" />1. What are cookies?</h2>
          <p>
            "Cookies" are small text files placed on your device (computer, tablet, phone) by
            the websites you visit. Together with related technologies — such as HTML5
            localStorage, sessionStorage, IndexedDB, pixel tags, web beacons, SDKs and
            fingerprinting techniques (collectively, "Tracking Technologies") — they allow a
            site to recognise your browser, remember your preferences and measure how the
            site is used. Cookies can be <strong>first-party</strong> (set by the domain
            you are visiting) or <strong>third-party</strong> (set by another domain whose
            content is embedded in the page), and <strong>session</strong> (deleted when
            you close your browser) or <strong>persistent</strong> (stored for a defined
            period stated in the tables below).
          </p>

          <h2 id="controller"><ShieldCheck className="mr-2 inline h-5 w-5" />2. Data controller</h2>
          <p>
            The data controller for personal data processed via cookies on this Site is
            <strong> FIXHEN</strong>, contactable at{" "}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. Where a third-party
            provider is listed in section 4, that provider acts as an independent controller
            or as a joint controller for its own portion of the processing; its terms and
            privacy notices apply in addition to this Policy.
          </p>

          <h2 id="legal"><Lock className="mr-2 inline h-5 w-5" />3. Legal basis</h2>
          <p>
            We rely on the following legal bases under Articles 6(1) GDPR and 5(3) of the
            ePrivacy Directive:
          </p>
          <ul>
            <li>
              <strong>Strictly necessary cookies:</strong> exempt from consent under Art. 5(3)
              ePrivacy Directive as they are strictly necessary for the provision of a service
              expressly requested by you (i.e. browsing the Site, adding items to cart,
              signing in, completing checkout).
            </li>
            <li>
              <strong>Functional, analytics, marketing and operational third-party cookies:</strong>{" "}
              your prior, freely given, specific, informed and unambiguous consent obtained
              through the cookie preferences panel, in line with the CJEU judgment in
              <em> Planet49</em> (C-673/17) and EDPB Guidelines 05/2020 on consent.
            </li>
            <li>
              Where relevant, we may also rely on our <strong>legitimate interests</strong>
              (Art. 6(1)(f) GDPR) for security, fraud prevention and network integrity,
              subject to an appropriate balancing test.
            </li>
          </ul>

          <h2 id="categories"><Cookie className="mr-2 inline h-5 w-5" />4. Categories of cookies we use</h2>
          <p>
            The examples in the tables below are indicative — exact cookie names, providers
            and durations may vary over time. The current live inventory is always available
            in the cookie preferences panel accessible from the footer.
          </p>

          {CATEGORIES.map((cat) => (
            <div key={cat.id} id={cat.id} className="not-prose mt-8 rounded-xl border border-border bg-card p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="rounded-lg bg-primary/10 p-2 text-primary">{cat.icon}</span>
                  <div>
                    <h3 className="text-base font-bold text-foreground">{cat.name}</h3>
                    <p className="mt-1 text-xs text-muted-foreground">{cat.legalBasis}</p>
                  </div>
                </div>
                <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-widest ${cat.required ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" : "bg-muted text-muted-foreground"}`}>
                  {cat.required ? "Always on" : "Optional"}
                </span>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{cat.purpose}</p>
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-border text-[11px] uppercase tracking-widest text-muted-foreground">
                      <th className="py-2 pr-3 font-semibold">Name</th>
                      <th className="py-2 pr-3 font-semibold">Provider</th>
                      <th className="py-2 pr-3 font-semibold">Type</th>
                      <th className="py-2 pr-3 font-semibold">Purpose</th>
                      <th className="py-2 pr-3 font-semibold">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cat.examples.map((e) => (
                      <tr key={e.name} className="border-b border-border/50 last:border-0">
                        <td className="py-2 pr-3 font-mono text-[11px] text-foreground">{e.name}</td>
                        <td className="py-2 pr-3 text-muted-foreground">{e.provider}</td>
                        <td className="py-2 pr-3 text-muted-foreground">{e.type}</td>
                        <td className="py-2 pr-3 text-muted-foreground">{e.purpose}</td>
                        <td className="py-2 pr-3 text-muted-foreground">{e.duration}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          <h2 id="manage"><Settings2 className="mr-2 inline h-5 w-5" />5. How to manage your preferences</h2>
          <p>
            You can accept, refuse or fine-tune every non-essential category through the
            cookie preferences panel presented on your first visit, or reopened at any time
            via the <strong>“Cookie settings”</strong> link in the footer. Withdrawal of
            consent is as easy as giving it and takes effect for the future only; it does
            not affect the lawfulness of processing carried out on the basis of consent
            previously given.
          </p>
          <p>
            You can additionally control cookies through your browser settings — including
            deleting existing cookies, blocking cookies from specific domains, or refusing
            all third-party cookies. Instructions are available for{" "}
            <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noreferrer">Chrome</a>,{" "}
            <a href="https://support.mozilla.org/kb/enhanced-tracking-protection-firefox-desktop" target="_blank" rel="noreferrer">Firefox</a>,{" "}
            <a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" target="_blank" rel="noreferrer">Safari</a> and{" "}
            <a href="https://support.microsoft.com/microsoft-edge" target="_blank" rel="noreferrer">Edge</a>.
            Please note that blocking strictly necessary cookies via your browser will
            prevent parts of the Site from functioning correctly, and we cannot be held
            liable for the resulting loss of functionality.
          </p>
          <p>
            We honour Global Privacy Control (GPC) signals for the categories that legally
            require consent, treating a valid GPC signal as an opt-out from all non-essential
            categories.
          </p>

          <h2 id="retention"><Clock3 className="mr-2 inline h-5 w-5" />6. Retention periods</h2>
          <p>
            Retention periods are stated per cookie in the tables above. Session cookies are
            deleted automatically when you close the browser. Persistent cookies expire on
            the date shown or when you clear them manually. Aggregate analytics data derived
            from cookies is retained for up to 26 months, after which it is either deleted
            or fully anonymised so that re-identification is no longer reasonably possible.
          </p>

          <h2 id="transfers"><Globe2 className="mr-2 inline h-5 w-5" />7. International transfers</h2>
          <p>
            Where a third-party provider processes personal data outside the European
            Economic Area, the transfer is safeguarded by (i) an EU adequacy decision, (ii)
            the European Commission's Standard Contractual Clauses (Decision 2021/914) with,
            where appropriate, supplementary technical and organisational measures, or (iii)
            certification under the EU-US Data Privacy Framework. Copies of the relevant
            safeguards are available on request at{" "}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
          </p>

          <h2 id="rights"><ShieldCheck className="mr-2 inline h-5 w-5" />8. Your data-protection rights</h2>
          <p>
            Where cookies process personal data you are, subject to statutory conditions,
            entitled to: access (Art. 15 GDPR), rectification (Art. 16), erasure
            (Art. 17), restriction (Art. 18), portability (Art. 20), objection (Art. 21),
            and to withdraw consent at any time (Art. 7(3)). You may also lodge a complaint
            with the Bulgarian Commission for Personal Data Protection (KZLD) or with the
            supervisory authority of your habitual residence. Contact us at{" "}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> to exercise these rights;
            we respond within one month, extendable by two further months for complex
            requests.
          </p>

          <h2 id="dnt"><AlertTriangle className="mr-2 inline h-5 w-5" />9. Do Not Track</h2>
          <p>
            The "Do Not Track" HTTP header is not a settled standard and browsers implement
            it inconsistently. We therefore do not treat a DNT signal as a substitute for
            the consent obtained through the cookie preferences panel. We do, however,
            honour Global Privacy Control (GPC) signals as set out in section 5.
          </p>

          <h2 id="changes"><RefreshCcw className="mr-2 inline h-5 w-5" />10. Changes to this Policy</h2>
          <p>
            We may update this Cookie Policy to reflect changes in law, technology or the
            services we use. The version in force is always available at{" "}
            <a href={PAGE_URL}>{PAGE_URL}</a>, with an updated "Last updated" date.
            Material changes to the categories, purposes or third parties involved will
            trigger a fresh consent request through the preferences panel.
          </p>

          <h2 id="faq"><Cookie className="mr-2 inline h-5 w-5" />11. Frequently asked questions</h2>
          <div className="not-prose space-y-3">
            {FAQS.map((f) => (
              <details key={f.q} className="group rounded-lg border border-border bg-card p-4 open:shadow-sm">
                <summary className="cursor-pointer list-none text-sm font-semibold text-foreground marker:hidden">
                  <span className="mr-2 text-primary group-open:hidden">+</span>
                  <span className="mr-2 hidden text-primary group-open:inline">−</span>
                  {f.q}
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>

          <h2 id="contact"><Mail className="mr-2 inline h-5 w-5" />12. Contact</h2>
          <p>
            Data protection & privacy queries — <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
            For an overview of our full data-processing activities, please also see our{" "}
            <Link to="/terms">Terms & Conditions</Link> and{" "}
            <Link to="/returns">Returns Policy</Link>.
          </p>
        </article>

        <aside className="mt-12 rounded-2xl border border-border bg-gradient-to-br from-primary/5 to-transparent p-6">
          <h3 className="text-lg font-bold text-foreground">Manage your cookie preferences</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Open the preferences panel from the footer or contact{" "}
            <a className="font-medium text-primary hover:underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>{" "}
            with any question about how we handle your data.
          </p>
          <ErrorTrackingToggle />
        </aside>
      </main>
      <SiteFooter />
    </div>
  );
}

function SummaryCard({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center gap-2 text-primary">
        {icon}
        <span className="text-sm font-bold text-foreground">{title}</span>
      </div>
      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{body}</p>
    </div>
  );
}

function ErrorTrackingToggle() {
  const [optedOut, setOptedOut] = useState<boolean | null>(null);
  useEffect(() => setOptedOut(isTrackingOptedOut()), []);
  if (optedOut === null) return null;
  return (
    <div className="mt-4 flex items-start justify-between gap-3 rounded-xl border border-border bg-card p-4">
      <div className="min-w-0">
        <div className="text-sm font-bold text-foreground">Anonymous error diagnostics</div>
        <p className="mt-1 text-xs text-muted-foreground">
          Helps us reproduce and fix real bugs (checkout, cart, payment). No personal data —
          only the error, the URL and a short breadcrumb of your recent clicks. You can opt
          out any time.
        </p>
      </div>
      <label className="inline-flex items-center gap-2 shrink-0 cursor-pointer text-xs font-semibold">
        <input
          type="checkbox"
          checked={!optedOut}
          onChange={(e) => {
            const next = !e.target.checked;
            setTrackingOptOut(next);
            setOptedOut(next);
          }}
          className="h-4 w-4 accent-primary"
        />
        {optedOut ? "Off" : "On"}
      </label>
    </div>
  );
}